/**
 * CollisionSystem - Pure collision detection
 *
 * Handles ONLY detection, not resolution.
 * All functions are pure - no side effects.
 */

export class CollisionSystem {
  constructor(tileSize = 12) {
    this.TILE_SIZE = tileSize;
  }

  /**
   * Check if a rectangle overlaps any solid tile
   * @param {Function} getTileSolid - Function to check if tile at (cx, cy, tx, ty) is solid
   * @param {number} x - Rectangle x
   * @param {number} y - Rectangle y
   * @param {number} w - Rectangle width
   * @param {number} h - Rectangle height
   * @param {number} groundTopY - Y position of ground surface
   * @param {Function} checkAboveGround - Optional function to check non-tile solids above ground (e.g., planks)
   * @returns {boolean}
   */
  rectOverlapsSolid(getTileSolid, x, y, w, h, groundTopY, checkAboveGround = null) {
    // Fast reject: above ground surface (unless we need to check for planks/platforms)
    if (y + h <= groundTopY) {
      // Check for non-tile solids above ground (planks, platforms, etc.)
      if (checkAboveGround) return checkAboveGround(x, y, w, h);
      return false;
    }

    const x1 = Math.floor(x);
    const y1 = Math.max(groundTopY, Math.floor(y));
    const x2 = Math.floor(x + w);
    const y2 = Math.floor(y + h);

    const tileLeft = Math.floor(x1 / this.TILE_SIZE);
    const tileRight = Math.floor(x2 / this.TILE_SIZE);
    const tileTop = Math.floor((y1 - groundTopY) / this.TILE_SIZE);
    const tileBottom = Math.floor((y2 - groundTopY) / this.TILE_SIZE);

    for (let tx = tileLeft; tx <= tileRight; tx++) {
      for (let ty = tileTop; ty <= tileBottom; ty++) {
        if (getTileSolid(tx, ty)) return true;
      }
    }
    return false;
  }

  /**
   * Get collision info for a moving rectangle
   * @returns {Object} { overlapping: boolean, direction: string, depth: number }
   */
  getCollisionInfo(getTileSolid, x, y, w, h, vx, vy, groundTopY) {
    const margin = 1; // Check inner area to avoid edge cases
    const overlapping = this.rectOverlapsSolid(
      getTileSolid,
      x + margin,
      y + margin,
      Math.max(2, w - margin * 2),
      Math.max(2, h - margin * 2),
      groundTopY
    );

    if (!overlapping) {
      return { overlapping: false, direction: null, depth: 0 };
    }

    // Determine collision direction based on velocity
    let direction = 'center';
    if (Math.abs(vx) > Math.abs(vy)) {
      direction = vx > 0 ? 'right' : 'left';
    } else if (vy !== 0) {
      direction = vy > 0 ? 'down' : 'up';
    }

    return { overlapping: true, direction, depth: 1 };
  }

  /**
   * Check if position has ground support below
   */
  hasGroundSupport(getTileSolid, x, y, w, h, groundTopY, checkAboveGround = null) {
    return this.rectOverlapsSolid(
      getTileSolid,
      x + 1,
      y + h + 1,
      Math.max(1, w - 2),
      2,
      groundTopY,
      checkAboveGround
    );
  }

  /**
   * Check if blocked by wall ahead
   */
  isBlockedAhead(getTileSolid, x, y, w, h, direction, groundTopY) {
    const checkX = direction > 0 ? (x + w) : (x - 2);
    return this.rectOverlapsSolid(
      getTileSolid,
      checkX,
      y + 2,
      3,
      h - 4,
      groundTopY
    );
  }

  /**
   * Find nearest clear position (for unstuck)
   * @returns {Object} { x, y, found: boolean }
   */
  findNearestClearPosition(getTileSolid, x, y, w, h, groundTopY, maxDistance = 4, preferredDir = 0, isUnderground = false, checkAboveGround = null) {
    // Try positions in expanding radius, biasing toward moving forward/downward only (never up).
    const dirs = [];
    const seen = new Set();
    const add = (dx, dy) => {
      const key = `${dx},${dy}`;
      if (seen.has(key) || (dx === 0 && dy === 0) || dy < 0) return; // disallow upward moves
      seen.add(key);
      dirs.push([dx, dy]);
    };
    const pd = Math.sign(preferredDir || 0);
    const forwardFirst = pd !== 0;
    // Underground: favor down and forward-down
    if (isUnderground) {
      add(0, 1);
      if (forwardFirst) add(pd, 0);
      if (forwardFirst) add(pd, 1);
      add(1, 0); add(-1, 0);
      add(1, 1); add(-1, 1);
      if (forwardFirst) add(-pd, 1);
    } else {
      add(0, -1);
      add(0, 1);
      if (forwardFirst) add(pd, 0);
      add(1, 0); add(-1, 0);
      add(1, 1); add(-1, 1); add(1, -1); add(-1, -1);
      if (forwardFirst) add(-pd, 0);
    }

    for (let dist = 1; dist <= maxDistance; dist++) {
      for (const [dx, dy] of dirs) {
        const testX = x + dx * dist;
        const testY = y + dy * dist;

        if (!this.rectOverlapsSolid(getTileSolid, testX, testY, w, h, groundTopY, checkAboveGround)) {
          return { x: testX, y: testY, found: true };
        }
      }
    }

    return { x, y, found: false };
  }
}
